/**
 * Shared Footer Component
 * Provides consistent footer functionality across all portals
 */

import React from 'react';
import { Box, Typography, Link, Divider, useTheme, alpha, Stack } from '@mui/material';
import { useLayout } from '../LayoutProvider';

/**
 * Footer Component
 * @param {Object} props - Component props
 * @param {Array} props.links - Footer links
 * @param {string} props.copyright - Copyright text
 * @param {ReactNode} props.children - Custom footer content
 * @param {boolean} props.showDivider - Whether to show top divider
 * @param {string} props.variant - Footer variant ('minimal', 'full')
 */
export const Footer = ({
  links = [],
  copyright,
  children,
  showDivider = true,
  variant = 'full',
}) => {
  const theme = useTheme();
  const { layoutConfig, portalType, isDark } = useLayout();

  // Default footer links based on portal
  const defaultLinks = getDefaultFooterLinks(portalType);
  const footerLinks = links.length > 0 ? links : defaultLinks;

  // Default copyright
  const defaultCopyright = `© ${new Date().getFullYear()} FleetCam. All rights reserved.`;
  const copyrightText = copyright || defaultCopyright;

  // Minimal footer for specific portals
  if (variant === 'minimal') {
    return (
      <Box
        component="footer"
        sx={{
          py: 1,
          px: 2,
          mt: 'auto',
          textAlign: 'center',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          borderTop: showDivider ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {copyrightText}
        </Typography>
      </Box>
    );
  }

  // Full footer
  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.95)
          : alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(10px)',
        borderTop: showDivider ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
      }}
    >
      {/* Custom Content */}
      {children && <Box sx={{ p: 2 }}>{children}</Box>}

      {/* Footer Links and Copyright */}
      <Box sx={{ p: 2 }}>
        {/* Links */}
        {footerLinks.length > 0 && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            {footerLinks.map((link, index) => (
              <React.Fragment key={link.key || index}>
                <Link
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  color="text.secondary"
                  underline="hover"
                  variant="body2"
                  sx={{
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {link.label}
                </Link>

                {/* Divider for desktop */}
                {index < footerLinks.length - 1 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      height: 16,
                      alignSelf: 'center',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Stack>
        )}

        {/* Copyright */}
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: footerLinks.length > 0 ? 1 : 0 }}
        >
          {copyrightText}
        </Typography>

        {/* Portal/Version Info */}
        {process.env.NODE_ENV === 'development' && (
          <Typography
            variant="caption"
            color="text.disabled"
            align="center"
            display="block"
            sx={{ mt: 0.5 }}
          >
            {portalType} portal • v{process.env.REACT_APP_VERSION || '1.0.0'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * Get default footer links for portal
 * @param {string} portalType - Portal type
 * @returns {Array} Default footer links
 */
const getDefaultFooterLinks = (portalType) => {
  const commonLinks = [
    { label: 'Privacy Policy', href: '/privacy', key: 'privacy' },
    { label: 'Terms of Service', href: '/terms', key: 'terms' },
    { label: 'Support', href: '/support', key: 'support' },
  ];

  const portalSpecificLinks = {
    bureau: [{ label: 'Documentation', href: '/docs/bureau', key: 'docs' }, ...commonLinks],
    admin: [
      { label: 'Admin Guide', href: '/docs/admin', key: 'admin-guide' },
      { label: 'API Documentation', href: '/docs/api', key: 'api-docs' },
      ...commonLinks,
    ],
    client: [{ label: 'User Guide', href: '/docs/client', key: 'user-guide' }, ...commonLinks],
    'super-admin': [
      {
        label: 'System Documentation',
        href: '/docs/system',
        key: 'system-docs',
      },
      { label: 'Security Center', href: '/security', key: 'security' },
      ...commonLinks,
    ],
  };

  return portalSpecificLinks[portalType] || commonLinks;
};

/**
 * Sticky Footer Component
 * Ensures footer sticks to bottom of viewport
 */
export const StickyFooter = (props) => {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: '100vh',
      }}
    >
      <Footer {...props} />
    </Box>
  );
};

export default Footer;
